/**
 * Runtime character model — parse a champion GLB, normalise its scale, and pick
 * clips from sim-derived state (#237, first slice of #231).
 *
 * This module is GPU-free: it parses a GLB `ArrayBuffer` into a Three scene
 * graph + animation clips and does the (pure) scale / animation-selection math.
 * The WebGL layer (`threeLayer.ts`) owns the renderer, mixer, and per-frame
 * updates. Splitting it this way lets the loader + math be unit-tested headless
 * (jsdom / node GLTF parse), which is the acceptance gate.
 *
 * RIP-OUT CONTRACT: `parseCharacterModel` NEVER throws. A missing / malformed /
 * empty GLB resolves to `null`, and every caller falls back to the existing
 * procedural / sprite renderer. Deleting the GLB returns the game to today's
 * visuals.
 *
 * PRESENTATION ONLY — never reads or writes sim state or any hash.
 */

import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { CHARACTER_TARGET_HEIGHT_UNITS, DEFAULT_BODY_RADIUS_UNITS } from "../projection.js";
import type { SpriteAnimationName } from "../championSprites.js";
import { chooseAnimation, type EntityAnimState } from "../spriteAnimation.js";

/**
 * Target on-floor height for a champion model, in SIM units — derived from the
 * collision body (`CHARACTER_TARGET_HEIGHT_UNITS`, its canonical home in
 * `projection.ts`), NEVER a hand-tuned per-model number. Re-exported here so
 * existing 3D-side importers keep working. Any GLB (whatever arbitrary baked
 * scale it ships — Meshy exports carry ~28×) is scaled by bounding box to hit
 * this height.
 */
export { CHARACTER_TARGET_HEIGHT_UNITS };

/** Clip crossfade duration when the animation changes (walk↔idle). */
export const CROSSFADE_SECONDS = 0.12;

/**
 * COSMETIC playback-rate multiplier for the `walk` clip ONLY (#840). `1.0` is
 * "exactly as authored"; `1.2` cycles the legs 20% faster. One number, one
 * place — every champion inherits it, no per-GLB or per-champion table.
 *
 * ⚠️ THIS IS ART TEMPO, NOT MOVEMENT SPEED. How fast a unit actually crosses
 * the arena is simulated server-side (`projectileSpeedScaleBp` in `tuning.json`,
 * hashed, `SIM_VERSION`-affecting — see #828); the client only copies
 * snapshot positions into `root.position` (`threeLayer.ts`). Nothing here may
 * ever feed movement, facing, or timing: this value must reach ONLY
 * `AnimationAction.setEffectiveTimeScale`. In particular it must NEVER scale
 * the `dtSeconds` handed to `mixer.update` — that is the real frame delta and
 * also drives `FACING_LERP_RATE`, so scaling it would make TURNING speed track
 * the art knob (and would speed up every clip, see below).
 */
export const WALK_ANIMATION_RATE = 1.0;

/**
 * Accepted range for an animation rate. A rate <= 0 is not a slower walk: `0`
 * freezes the clip and a negative value plays it BACKWARDS, and an absurdly
 * large one is a typo, not a style. `clampAnimationRate` folds both into
 * something playable rather than letting a bad edit ship a frozen champion.
 */
export const MIN_ANIMATION_RATE = 0.1;
export const MAX_ANIMATION_RATE = 4;

/**
 * Sanity-bound a configured animation rate to `[MIN_ANIMATION_RATE,
 * MAX_ANIMATION_RATE]`. A non-finite or non-positive rate degrades to the
 * no-op `1` — the same "a degenerate input returns the identity" guard
 * `computeUniformScale` uses — so a bad constant is visually neutral instead
 * of freezing or reversing the clip.
 */
export function clampAnimationRate(rate: number): number {
  if (!Number.isFinite(rate) || rate <= 0) return 1;
  return Math.min(MAX_ANIMATION_RATE, Math.max(MIN_ANIMATION_RATE, rate));
}

/**
 * Playback rate for one clip kind. WALK-ONLY BY CONSTRUCTION (#840): `idle`,
 * `cast` and `death` are pinned at `1`, and that is load-bearing, not an
 * oversight —
 *   · `death` is `LoopOnce` + `clampWhenFinished` (#830), so accelerating it
 *     snaps corpses to the floor unnaturally;
 *   · `cast` reads as tied to the (sim-timed, 30–90 tick) cast, so speeding
 *     the art up desyncs it from the cast bar (#836).
 * Widening this to a per-clip-kind table later is a one-line change here and
 * touches nothing else.
 *
 * `walkRate` is a parameter purely so the walk-only contract is testable at a
 * rate other than the shipped default; production always passes the default.
 */
export function animationTimeScaleFor(
  clip: Character3DAnimation,
  walkRate: number = WALK_ANIMATION_RATE,
): number {
  return clip === "walk" ? clampAnimationRate(walkRate) : 1;
}

/**
 * The minimal `THREE.AnimationAction` surface an animation transition touches.
 * Declared structurally so the transition can be unit-tested against a plain
 * fake — no WebGL context, no mixer, no GLB.
 */
export interface AnimationActionLike<Self = unknown> {
  enabled: boolean;
  reset: () => unknown;
  play: () => unknown;
  setEffectiveTimeScale: (scale: number) => unknown;
  setEffectiveWeight: (weight: number) => unknown;
  // Self-referential: `THREE.AnimationAction.crossFadeTo` takes a CONCRETE
  // `AnimationAction`, so a parameter typed as this interface would be an
  // unsound widening (TS checks it contravariantly and rejects it). The
  // type parameter lets the real action satisfy the shape as itself.
  crossFadeTo: (to: Self, seconds: number, warp: boolean) => unknown;
}

/**
 * Drive one clip-to-clip transition: rewind + start `to`, give it FULL weight,
 * and crossfade `from` out. Extracted from the layer purely so the weight
 * handling is testable — `threeLayer.ts` owns the renderer and cannot run
 * headless.
 *
 * WHY `setEffectiveWeight(1)` IS LOAD-BEARING (and not redundant with the
 * crossfade): `crossFadeTo` ramps the target's weight from its CURRENT value
 * toward that same current value — it does not impose a destination of 1. Every
 * non-initial action is parked at weight 0 when the instance is built, so
 * fading straight into one interpolates 0 -> 0: the clip advances and drives
 * its bones with ZERO influence, leaving the model visibly stuck in `idle`.
 *
 * That is exactly the "walk and cast don't work, idle does" bug, and it is
 * older than the multi-clip refactor — the original two-clip version had the
 * same defect, so the rogue's walk never animated either.
 *
 * Passing `from === null` (nothing currently playing) hard-sets the weight
 * instead of fading, which is the same thing the fade converges to.
 *
 * `clip` (#840) is the CLIP IDENTITY of `to`. This is the shared entry point
 * for all four clips, so without it the playback rate could only be applied
 * globally — which is exactly wrong for `death` and `cast` (see
 * `animationTimeScaleFor`). It is passed in explicitly by `setAnimation`, the
 * only caller and the only place that knows the clip; it is NEVER inferred
 * from the action.
 */
export function applyAnimationTransition<A extends AnimationActionLike<A>>(
  from: A | null,
  to: A,
  clip: Character3DAnimation,
  crossfadeSeconds: number = CROSSFADE_SECONDS,
  walkRate: number = WALK_ANIMATION_RATE,
): void {
  to.enabled = true;
  to.setEffectiveTimeScale(animationTimeScaleFor(clip, walkRate));
  // Rewind so a clip always starts at frame 0 — and so a one-shot `death` that
  // already ran is not left parked on its clamped final frame.
  to.reset();
  to.play();
  to.setEffectiveWeight(1);
  if (from !== null) {
    from.crossFadeTo(to, crossfadeSeconds, false);
  }
}

/** A parsed, placement-ready character model. */
export interface LoadedCharacterModel {
  /**
   * The placement handle. Already normalised: uniformly scaled to
   * `CHARACTER_TARGET_HEIGHT_UNITS`, feet at local y = 0, centred on x/z. The
   * layer sets `.position` (world) and `.rotation.y` (facing) on this node.
   */
  readonly root: THREE.Group;
  /** Animation clips shipped in the GLB, in file order. */
  readonly clips: readonly THREE.AnimationClip[];
}

/**
 * Uniform scale factor that maps a model of `modelHeight` (in its own units) to
 * `targetHeight`. Guards a degenerate (non-positive) height by returning 1 so a
 * malformed bounding box can never divide-by-zero or invert the model. Pure.
 */
export function computeUniformScale(modelHeight: number, targetHeight: number): number {
  if (!(modelHeight > 0) || !(targetHeight > 0)) return 1;
  return targetHeight / modelHeight;
}

/**
 * Live per-instance scale multiplier (#342) layered on TOP of the model's
 * baked normalization (`normaliseToPlacementRoot`, which fixes the model to
 * `CHARACTER_TARGET_HEIGHT_UNITS` — itself derived from
 * `DEFAULT_BODY_RADIUS_UNITS`). A live, aura-modified `radius` (e.g. Stand
 * Behind Me's +30%) that has grown/shrunk relative to the champion's BASE
 * radius scales the model by that same ratio, so a size-changing statMod
 * visibly scales the 3D model too, not just the 2D fallback paths.
 *
 * Guards a degenerate (non-positive) `radius` or `baseRadius` the same way
 * `computeUniformScale` guards a degenerate height — returning the no-op
 * scale of 1 rather than dividing by zero or inverting the model.
 */
export function computeLiveRadiusScale(
  radius: number,
  baseRadius: number = DEFAULT_BODY_RADIUS_UNITS,
): number {
  if (!(radius > 0) || !(baseRadius > 0)) return 1;
  return radius / baseRadius;
}

/**
 * The clips the 3D path can select. A SUBSET of `SpriteAnimationName` — the 2D
 * sheet also bakes `attack`/`hurt`, which no character GLB ships yet; adding one
 * is a matter of widening this union and shipping the clip, with no change to
 * the selection logic below.
 */
export type Character3DAnimation = Extract<
  SpriteAnimationName,
  "idle" | "walk" | "cast" | "attack" | "death"
>;

/** The always-present fallback clip (see `README.md`'s GLB contract). */
export const FALLBACK_3D_ANIMATION: Character3DAnimation = "idle";

/**
 * Every clip the 3D path looks for, in build order. `threeLayer.ts` iterates
 * this to build one `AnimationAction` per clip a GLB actually ships.
 */
export const CHARACTER_3D_ANIMATION_NAMES: readonly Character3DAnimation[] = [
  "idle",
  "walk",
  "cast",
  "attack",
  "death",
];

/** `Character3DAnimation` as a runtime set, for narrowing the 2D chooser. */
const CHARACTER_3D_ANIMATIONS: ReadonlySet<string> = new Set<Character3DAnimation>(
  CHARACTER_3D_ANIMATION_NAMES,
);

/**
 * Choose the animation for a champion from its (cosmetic, snapshot-derived)
 * state, by the SAME priority the 2D sprite renderer uses:
 *   dead → `death` · casting → `cast` · moving → `walk` · else → `idle`.
 *
 * Deliberately DELEGATES to `chooseAnimation` (`spriteAnimation.ts`) instead of
 * restating the priority ladder: "what is this character doing" must have ONE
 * definition, or 2D and 3D drift apart as states are added. This narrows that
 * result to the clips a GLB can carry, falling back to `idle` for a 2D-only
 * animation (`attack`/`hurt`) — mirroring `resolveAnimation`'s contract that a
 * missing block degrades to the always-present `idle` rather than indexing
 * nothing.
 *
 * Whether the CHOSEN clip actually exists in a given GLB is a separate concern,
 * handled by `findClip` returning `null` (see `threeLayer.ts` `setAnimation`).
 */
export function choose3DAnimation(state: EntityAnimState): Character3DAnimation {
  const want = chooseAnimation(state);
  return CHARACTER_3D_ANIMATIONS.has(want) ? (want as Character3DAnimation) : FALLBACK_3D_ANIMATION;
}

/**
 * Find a clip by name, case-insensitively, trying each candidate in order.
 * Returns the first match, or `null` if none of the names are present (so a GLB
 * missing the requested clip degrades instead of throwing).
 */
export function findClip(
  clips: readonly THREE.AnimationClip[],
  ...names: readonly string[]
): THREE.AnimationClip | null {
  for (const want of names) {
    const lower = want.toLowerCase();
    const hit = clips.find((c) => c.name.toLowerCase() === lower);
    if (hit !== undefined) return hit;
  }
  return null;
}

/** True if the sub-tree contains at least one renderable mesh. */
export function hasMesh(object: THREE.Object3D): boolean {
  let found = false;
  object.traverse((o) => {
    if (o instanceof THREE.Mesh) found = true;
  });
  return found;
}

/**
 * Give every mesh in `root` its OWN material instances (#923).
 *
 * WHY THIS IS REQUIRED, not defensive: `SkeletonUtils.clone` (how
 * `threeLayer.ts` instantiates a character) shares the source model's
 * materials across every clone — that is the point, it keeps the GPU state
 * shared. But per-INSTANCE opacity is a material property, so dimming one
 * stealthed rogue would dim EVERY entity cloned from the rogue GLB, including
 * the enemy rogue standing next to it. Cloning materials per instance is the
 * only way to make opacity per instance.
 *
 * GEOMETRY IS STILL SHARED (untouched here) — the expensive buffer stays one
 * upload; only the small material objects are duplicated, at most once per
 * character in the match (six in a 3v3), at instantiation, never per frame.
 *
 * Handles both single-material and material-array meshes. Pure w.r.t. the
 * source model: only the clone's meshes are mutated.
 */
export function cloneInstanceMaterials(root: THREE.Object3D): void {
  root.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    const mat: unknown = o.material;
    o.material = Array.isArray(mat)
      ? (mat as THREE.Material[]).map((m) => m.clone())
      : (mat as THREE.Material).clone();
  });
}

/**
 * Set a whole character instance's opacity (#923) — the 3D half of the
 * stealthed-but-visible treatment (`entityView.ts`'s `STEALTH_VISIBLE_ALPHA`
 * dims the 2D overlay; this dims the model itself).
 *
 * `alpha >= 1` restores the opaque path exactly: `transparent` goes back to
 * `false` and `depthWrite` to `true`, so a champion leaving stealth is
 * byte-identical to one that never entered it — a model left in the
 * transparent blend path sorts differently and would silently change how
 * every opaque champion draws.
 *
 * `depthWrite = false` while transparent is what stops the model's own far
 * side from z-fighting through its near side once it is see-through (the
 * standard Three treatment for a blended mesh).
 *
 * ⚠️ Only safe on a root whose materials are its OWN — call
 * {@link cloneInstanceMaterials} at instantiation first, or this writes
 * through to every instance sharing the source model.
 *
 * Clamps to `[0, 1]` (a non-finite alpha degrades to fully opaque, the same
 * "a degenerate input returns the identity" guard `computeUniformScale` uses).
 */
export function applyModelOpacity(root: THREE.Object3D, alpha: number): void {
  const a = !Number.isFinite(alpha) ? 1 : alpha < 0 ? 0 : alpha > 1 ? 1 : alpha;
  const transparent = a < 1;
  root.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    const mat: unknown = o.material;
    const materials: THREE.Material[] = Array.isArray(mat)
      ? (mat as THREE.Material[])
      : [mat as THREE.Material];
    for (const m of materials) {
      m.transparent = transparent;
      m.opacity = a;
      m.depthWrite = !transparent;
      m.needsUpdate = true;
    }
  });
}

/**
 * Force `scene`'s skeleton (if any) onto the `idle` clip's first frame before
 * it's measured (#246). A GLTF's loaded scene starts in whatever REST/BIND
 * pose the file's skeleton nodes carry — commonly a T-pose or A-pose for a
 * rigged export — which is NOT necessarily the standing pose the character
 * actually plays at runtime. Measuring the bounding box against that bind
 * pose can under/overshoot the true standing height and — worse — the true
 * lowest (foot) point, which is what `normaliseToPlacementRoot` zeroes to
 * ground the feet; a bad bind pose there is exactly the "floating feet" bug.
 *
 * A throwaway `AnimationMixer` evaluated at `t = 0` sets every animated
 * bone's LOCAL transform to the idle clip's opening frame — the pose the
 * character is actually shown standing in (idle plays immediately on spawn,
 * `threeLayer.ts` `makeInstance`). No-op (leaves the loaded bind pose as-is)
 * when there's no `idle` clip, so a GLB without one keeps today's behaviour.
 *
 * Never throws: a malformed clip can't take down the whole model (the rip-out
 * contract lives one level up, in `parseCharacterModel`), so any mixer error
 * here is swallowed and the (unposed) bind pose is measured instead —
 * degrading gracefully rather than losing the 3D model entirely.
 */
function poseForMeasurement(scene: THREE.Object3D, clips: readonly THREE.AnimationClip[]): void {
  const idleClip = findClip(clips, "idle");
  if (idleClip === null) return;
  try {
    const mixer = new THREE.AnimationMixer(scene);
    mixer.clipAction(idleClip).play();
    mixer.update(0);
    // Deliberately NOT `mixer.stopAllAction()` — that resets every animated
    // property back to its PRE-action value (undoing the pose we just set).
    // The mixer itself is thrown away; nothing ever calls `.update()` on it
    // again, so the bones simply stay at this frame until the real runtime
    // mixer (`threeLayer.ts` `makeInstance`) takes over and plays the same
    // clip from the same frame — idempotent.
  } catch (err) {
    console.error(
      "[pit] rogue GLB idle-pose evaluation failed — measuring the bind pose instead:",
      err,
    );
  }
}

/**
 * Normalise `scene` in place and wrap it in a placement handle: scale uniformly
 * so its bounding-box height becomes `targetHeight`, drop it so the feet sit at
 * local y = 0, and centre it on x/z. Returns the outer handle to position/rotate.
 *
 * The box is measured against the `idle` clip's opening frame, not the raw
 * loaded (bind/rest) pose — see `poseForMeasurement` — and with `precise`
 * `Box3` vertex sampling, so a `SkinnedMesh`'s bone-deformed vertices (the
 * idle pose) are what's actually measured, not its cached bind-pose geometry
 * box (#246).
 */
export function normaliseToPlacementRoot(
  scene: THREE.Object3D,
  targetHeight: number,
  clips: readonly THREE.AnimationClip[] = [],
): THREE.Group {
  poseForMeasurement(scene, clips);
  scene.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(scene, true);
  const size = new THREE.Vector3();
  const centre = new THREE.Vector3();
  box.getSize(size);
  box.getCenter(centre);

  const scale = computeUniformScale(size.y, targetHeight);
  scene.scale.multiplyScalar(scale);
  // After scaling about the origin, the box corners scale too: shift so the
  // (scaled) feet touch y = 0 and the body is centred on x/z.
  scene.position.set(-centre.x * scale, -box.min.y * scale, -centre.z * scale);

  const root = new THREE.Group();
  root.name = "CharacterRoot";
  root.add(scene);
  return root;
}

/**
 * Parse a champion GLB from raw bytes into a placement-ready model. Resolves to
 * `null` — never rejects — on malformed data, a mesh-less scene, or any loader
 * error, honouring the rip-out contract. `targetHeight` defaults to the
 * body-derived `CHARACTER_TARGET_HEIGHT_UNITS`.
 */
export async function parseCharacterModel(
  data: ArrayBuffer,
  targetHeight: number = CHARACTER_TARGET_HEIGHT_UNITS,
): Promise<LoadedCharacterModel | null> {
  const loader = new GLTFLoader();
  try {
    const gltf = await new Promise<{
      scene: THREE.Group;
      animations: THREE.AnimationClip[];
    }>((resolve, reject) => {
      loader.parse(
        data,
        "",
        (result) => {
          resolve(result);
        },
        (error: unknown) => {
          reject(error instanceof Error ? error : new Error(String(error)));
        },
      );
    });
    if (!hasMesh(gltf.scene)) {
      console.error("[pit] rogue GLB parsed but has no mesh — falling back to 2D renderer.");
      return null;
    }
    const root = normaliseToPlacementRoot(gltf.scene, targetHeight, gltf.animations);
    return { root, clips: gltf.animations };
  } catch (err) {
    console.error("[pit] rogue GLB failed to parse — falling back to 2D renderer:", err);
    return null;
  }
}
